import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCommentsForVideo, type VideoComment } from '@/mocks/videoComments';

interface VideoCommentsProps {
  videoId: string;
}

// Emoji reactions configuration
const EMOJI_REACTIONS = [
  { emoji: '❤️', name: 'love', color: 'text-rose-400' },
  { emoji: '😂', name: 'funny', color: 'text-amber-400' },
  { emoji: '😍', name: 'adorable', color: 'text-pink-400' },
  { emoji: '🐱', name: 'cat', color: 'text-orange-400' },
  { emoji: '😮', name: 'wow', color: 'text-sky-400' },
];

// Track reactions per comment: { commentId: { emoji: count } }
type ReactionCounts = Record<string, Record<string, number>>;
type UserReactions = Record<string, Set<string>>;

// Comprehensive bad words filter - blocks profanity and inappropriate content
const badWordsPatterns = [
  // Common profanity patterns (using regex to catch variations)
  /\b(f+u+c+k+|f+[*@#$%]+c+k+|fck|fuk|phuck|phuk)\w*/gi,
  /\b(s+h+[i1]+t+|sh[*@#$%]+t|sht)\w*/gi,
  /\b(b+[i1]+t+c+h+|b[*@#$%]+tch|btch)\w*/gi,
  /\b(a+s+s+h+o+l+e+|a+s+s+|@ss)\w*/gi,
  /\b(d+[i1]+c+k+|d[*@#$%]+ck|dck)\w*/gi,
  /\b(c+u+n+t+|c[*@#$%]+nt)\w*/gi,
  /\b(p+[i1]+s+s+|p[*@#$%]+ss)\w*/gi,
  /\b(damn|dammit|damm)\w*/gi,
  /\b(hell|heck)\b/gi,
  /\b(crap|crappy)\w*/gi,
  /\b(bastard|bstrd)\w*/gi,
  /\b(whore|wh0re|h0e|hoe)\w*/gi,
  /\b(slut|sl[*@#$%]+t)\w*/gi,
  /\b(retard|retarded|r3tard)\w*/gi,
  /\b(idiot|moron|stupid|dumb)\w*/gi,
  /\b(hate|hatred|hating)\s+(you|this|everyone|people)\b/gi,
  /\b(kill|murder|die|death)\s+(you|yourself|him|her|them)\b/gi,
  /\b(n+[i1]+g+[g@]+[e3a]+r*|n[*@#$%]+gg)\w*/gi,
  /\b(f+[a@]+g+[o0]*t*|f[*@#$%]+g)\w*/gi,
  // Spam patterns
  /\b(buy now|click here|free money|make money fast)\b/gi,
  // URL spam
  /(https?:\/\/|www\.)\S+/gi,
];

// Check if text contains bad words
const containsBadWords = (text: string): boolean => {
  return badWordsPatterns.some(pattern => pattern.test(text));
};

// Sanitize text - remove any HTML tags
const sanitizeText = (text: string): string => {
  return text.replace(/<[^>]*>/g, '').trim();
};

// Random cat avatar generator
const CAT_AVATAR_PROMPTS = [
  'adorable orange tabby cat face avatar, cartoon style, happy expression, soft pastel background, circular profile picture',
  'cute gray fluffy cat avatar icon, kawaii style, big eyes, simple design, circular profile picture',
  'sweet white persian cat face avatar, cartoon illustration, gentle expression, circular profile picture',
  'playful black cat avatar with yellow eyes, cute cartoon style, friendly look, circular profile picture',
  'charming calico cat face avatar, colorful patches, cartoon style, happy expression, circular profile picture',
  'lovely siamese cat avatar, blue eyes, elegant cartoon style, cream and brown colors, circular profile picture',
  'cute ginger kitten face avatar, big green eyes, fluffy fur, cartoon style, circular profile picture',
  'adorable tuxedo cat avatar, black and white, bowtie, cartoon style, circular profile picture',
  'sweet russian blue cat avatar, gray fur, green eyes, elegant cartoon style, circular profile picture',
  'playful scottish fold cat avatar, folded ears, round face, cute cartoon style, circular profile picture',
  'charming maine coon cat avatar, fluffy fur, majestic look, cartoon style, circular profile picture',
  'cute bengal cat avatar, spotted pattern, wild look, cartoon style, circular profile picture',
  'lovely ragdoll cat face avatar, blue eyes, fluffy, gentle expression, cartoon style, circular profile picture',
  'adorable british shorthair cat avatar, chubby cheeks, gray fur, cartoon style, circular profile picture',
  'sweet munchkin cat avatar, short legs reference, cute face, cartoon style, circular profile picture',
];

const getRandomCatAvatar = (): string => {
  const randomPrompt = CAT_AVATAR_PROMPTS[Math.floor(Math.random() * CAT_AVATAR_PROMPTS.length)];
  const uniqueSeq = `avatar-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28randomPrompt%29%7D&width=100&height=100&seq=${uniqueSeq}&orientation=squarish`;
};

export default function VideoComments({ videoId }: VideoCommentsProps) {
  const [comments, setComments] = useState<VideoComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'blocked'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [reactionCounts, setReactionCounts] = useState<ReactionCounts>({});
  const [userReactions, setUserReactions] = useState<UserReactions>({});
  const [activeReactionPicker, setActiveReactionPicker] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string>('');

  useEffect(() => {
    // Load comments for this video
    const videoComments = getCommentsForVideo(videoId);
    setComments(videoComments);
    
    // Initialize random reaction counts for mock data
    const initialCounts: ReactionCounts = {};
    videoComments.forEach(comment => {
      initialCounts[comment.id] = {
        '❤️': Math.floor(Math.random() * 20) + 5,
        '😂': Math.floor(Math.random() * 15),
        '😍': Math.floor(Math.random() * 12),
        '🐱': Math.floor(Math.random() * 8),
        '😮': Math.floor(Math.random() * 5),
      };
    });
    setReactionCounts(initialCounts);
    
    // Generate a random avatar for the current user
    setUserAvatar(getRandomCatAvatar());
  }, [videoId]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveReactionPicker(null);
    if (activeReactionPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeReactionPicker]);

  const handleReaction = (commentId: string, emoji: string) => {
    // Toggle user's reaction
    setUserReactions(prev => {
      const commentReactions = prev[commentId] || new Set();
      const newSet = new Set(commentReactions);
      
      if (newSet.has(emoji)) {
        newSet.delete(emoji);
      } else {
        newSet.add(emoji);
      }
      
      return { ...prev, [commentId]: newSet };
    });

    // Update counts
    setReactionCounts(prev => {
      const commentCounts = prev[commentId] || {};
      const currentCount = commentCounts[emoji] || 0;
      const hasReacted = userReactions[commentId]?.has(emoji);
      
      return {
        ...prev,
        [commentId]: {
          ...commentCounts,
          [emoji]: hasReacted ? currentCount - 1 : currentCount + 1,
        },
      };
    });

    setActiveReactionPicker(null);
  };

  const getTotalReactions = (commentId: string): number => {
    const counts = reactionCounts[commentId] || {};
    return Object.values(counts).reduce((sum, count) => sum + count, 0);
  };

  const getTopReactions = (commentId: string): { emoji: string; count: number }[] => {
    const counts = reactionCounts[commentId] || {};
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([emoji, count]) => ({ emoji, count }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const sanitizedComment = sanitizeText(newComment);
    const sanitizedUsername = sanitizeText(username);

    // Validate inputs
    if (!sanitizedUsername.trim() || !sanitizedComment.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (sanitizedUsername.length < 2 || sanitizedUsername.length > 30) {
      setSubmitStatus('error');
      setErrorMessage('Username must be 2-30 characters');
      return;
    }

    if (sanitizedComment.length < 3 || sanitizedComment.length > 500) {
      setSubmitStatus('error');
      setErrorMessage('Comment must be 3-500 characters');
      return;
    }

    // Check for bad words in both username and comment
    if (containsBadWords(sanitizedUsername) || containsBadWords(sanitizedComment)) {
      setSubmitStatus('blocked');
      setErrorMessage('Your comment contains inappropriate language. Please keep it friendly! 🐱');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Submit to form endpoint
      const formData = new URLSearchParams();
      formData.append('videoId', videoId);
      formData.append('username', sanitizedUsername);
      formData.append('comment', sanitizedComment);
      formData.append('timestamp', new Date().toISOString());

      const response = await fetch('https://readdy.ai/api/form/d68vlutbd17lru29l27g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        // Add comment to local state for immediate display with random cat avatar
        const newCommentId = `new-${Date.now()}`;
        const newCommentObj: VideoComment = {
          id: newCommentId,
          videoId,
          username: sanitizedUsername,
          avatar: userAvatar,
          comment: sanitizedComment,
          timestamp: 'Just now',
          likes: 0,
        };
        setComments(prev => [newCommentObj, ...prev]);
        
        // Initialize reactions for new comment
        setReactionCounts(prev => ({
          ...prev,
          [newCommentId]: { '❤️': 0, '😂': 0, '😍': 0, '🐱': 0, '😮': 0 },
        }));
        
        setNewComment('');
        setUsername('');
        setSubmitStatus('success');
        
        // Generate a new random avatar for the next comment
        setUserAvatar(getRandomCatAvatar());
        
        // Reset success message after 3 seconds
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch {
      setSubmitStatus('error');
      setErrorMessage('Failed to post comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = newComment.length;
  const maxCharacters = 500;

  return (
    <div className="border-t border-white/10 pt-4 mt-4">
      {/* Comments Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-chat-3-line text-sky-400 text-lg"></i>
        </div>
        <h3 className="font-heading text-lg font-semibold text-white">
          Comments ({comments.length})
        </h3>
        <span className="text-xs text-gray-500 font-body ml-2">
          <i className="ri-shield-check-line mr-1"></i>
          Moderated for safety
        </span>
      </div>

      {/* Comment Form */}
      <form 
        id="video-comment-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mb-6 bg-white/5 rounded-2xl p-4"
      >
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-sky-400/50">
            {userAvatar ? (
              <img 
                src={userAvatar} 
                alt="Your avatar" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-sky-400 to-teal-400 flex items-center justify-center">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your nickname"
                maxLength={30}
                className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-2 text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50 transition-all"
              />
              <button
                type="button"
                onClick={() => setUserAvatar(getRandomCatAvatar())}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-xs font-body"
                title="Get a new random cat avatar"
              >
                <i className="ri-refresh-line"></i>
                <span className="hidden sm:inline">New Avatar</span>
              </button>
            </div>
            <div className="relative">
              <textarea
                name="comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts... Keep it friendly! 🐱"
                maxLength={maxCharacters}
                rows={3}
                className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50 transition-all resize-none"
              />
              <span className={`absolute bottom-2 right-3 text-xs font-body ${
                characterCount > maxCharacters * 0.9 ? 'text-rose-400' : 'text-gray-500'
              }`}>
                {characterCount}/{maxCharacters}
              </span>
            </div>
            
            {/* Submit Button & Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-body">
                <i className="ri-information-line"></i>
                <span>Comments are moderated to keep our community safe</span>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !newComment.trim() || !username.trim()}
                className="px-5 py-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-400 hover:to-teal-400 text-white font-body text-sm font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Posting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Post Comment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        <AnimatePresence>
          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-3 flex items-center gap-2 text-emerald-400 font-body text-sm bg-emerald-500/10 rounded-xl px-4 py-2"
            >
              <i className="ri-checkbox-circle-line"></i>
              Comment posted successfully! Thanks for sharing! 🎉
            </motion.div>
          )}
          {submitStatus === 'blocked' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-3 flex items-center gap-2 text-amber-400 font-body text-sm bg-amber-500/10 rounded-xl px-4 py-2"
            >
              <i className="ri-error-warning-line"></i>
              {errorMessage}
            </motion.div>
          )}
          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-3 flex items-center gap-2 text-rose-400 font-body text-sm bg-rose-500/10 rounded-xl px-4 py-2"
            >
              <i className="ri-close-circle-line"></i>
              {errorMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* Comments List */}
      <div className="space-y-4 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
        {comments.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-3 bg-white/5 rounded-full flex items-center justify-center">
              <i className="ri-chat-smile-3-line text-gray-500 text-3xl"></i>
            </div>
            <p className="text-gray-500 font-body text-sm">No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          comments.map((comment, index) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex gap-3 group"
            >
              <img
                src={comment.avatar}
                alt={comment.username}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-body font-medium text-white text-sm truncate">
                    {comment.username}
                  </span>
                  <span className="text-gray-500 text-xs font-body">
                    {comment.timestamp}
                  </span>
                </div>
                <p className="text-gray-300 font-body text-sm leading-relaxed break-words">
                  {comment.comment}
                </p>
                
                {/* Emoji Reactions Section */}
                <div className="flex items-center gap-3 mt-2">
                  {/* Display existing reactions */}
                  <div className="flex items-center gap-1">
                    {getTopReactions(comment.id).map(({ emoji, count }) => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(comment.id, emoji)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all cursor-pointer ${
                          userReactions[comment.id]?.has(emoji)
                            ? 'bg-white/20 ring-1 ring-white/30'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <span>{emoji}</span>
                        <span className="text-gray-400">{count + (userReactions[comment.id]?.has(emoji) ? 1 : 0)}</span>
                      </button>
                    ))}
                    
                    {getTotalReactions(comment.id) > 0 && getTopReactions(comment.id).length < 3 && (
                      <span className="text-gray-500 text-xs font-body ml-1">
                        {getTotalReactions(comment.id)} reactions
                      </span>
                    )}
                  </div>

                  {/* Add Reaction Button */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveReactionPicker(activeReactionPicker === comment.id ? null : comment.id);
                      }}
                      className="flex items-center gap-1 text-xs font-body text-gray-500 hover:text-white transition-colors cursor-pointer px-2 py-1 rounded-full hover:bg-white/10"
                    >
                      <i className="ri-emotion-line"></i>
                      <span>React</span>
                    </button>

                    {/* Reaction Picker Popup */}
                    <AnimatePresence>
                      {activeReactionPicker === comment.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9, y: 5 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: 5 }}
                          className="absolute bottom-full left-0 mb-2 bg-gray-800 rounded-2xl p-2 shadow-xl border border-white/10 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-1">
                            {EMOJI_REACTIONS.map(({ emoji, name, color }) => (
                              <button
                                key={emoji}
                                onClick={() => handleReaction(comment.id, emoji)}
                                className={`w-10 h-10 flex items-center justify-center text-xl rounded-full transition-all cursor-pointer hover:bg-white/10 hover:scale-125 ${
                                  userReactions[comment.id]?.has(emoji) ? `bg-white/20 ${color}` : ''
                                }`}
                                title={name}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Reply Button */}
                  <button className="flex items-center gap-1 text-xs font-body text-gray-500 hover:text-sky-400 transition-colors cursor-pointer px-2 py-1 rounded-full hover:bg-white/10">
                    <i className="ri-reply-line"></i>
                    <span>Reply</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
